import { useQuery } from "@apollo/client/react";
import { useMemo } from "react";

import type { EventBySlugQuery } from "@/graphql/__generated__/graphql";
import type { EventRow } from "@/utils/schemas";

import { EVENT_BY_SLUG_QUERY } from "@/graphql/event-detail";

type EventNode = NonNullable<
  NonNullable<EventBySlugQuery["eventCollection"]>["edges"][number]["node"]
>;

export function useEventDetail(slug: string | undefined) {
  const { data, error, loading, refetch } = useQuery(EVENT_BY_SLUG_QUERY, {
    skip: !slug,
    variables: { slug: slug! },
  });

  const node = data?.eventCollection?.edges?.[0]?.node ?? null;

  const eventRow = useMemo(() => (node ? toEventRow(node) : null), [node]);

  const raw = useMemo(
    () => (eventRow ? (eventRow as unknown as Record<string, unknown>) : null),
    [eventRow],
  );

  return {
    error: error ? error.message : null,
    eventRow,
    loading,
    raw,
    refetch,
  };
}

function toEventRow(node: EventNode): EventRow {
  return {
    description: node.description ?? null,
    ends_at: node.ends_at ?? null,
    event_member:
      node.event_memberCollection?.edges.map((e) => ({
        role: e.node.role ?? "",
      })) ?? [],
    event_ticket_kind:
      node.event_ticket_kindCollection?.edges.map((e) => ({
        currency: e.node.currency ?? "",
        description: e.node.description ?? null,
        id: e.node.id ?? "",
        price_cents: e.node.price_cents ?? 0,
        price_service_fee: e.node.price_service_fee ?? null,
        quantity_sold: e.node.quantity_sold ?? null,
        quantity_total: e.node.quantity_total ?? null,
      })) ?? [],
    flyer_url: node.flyer_url ?? null,
    id: node.id ?? "",
    location_city: node.location_city ?? null,
    location_geo: node.location_geo ?? null,
    location_name: node.location_name ?? null,
    location_neighborhood: node.location_neighborhood ?? null,
    location_place_id: node.location_place_id ?? null,
    location_postal_code: node.location_postal_code ?? null,
    location_state: node.location_state ?? null,
    location_street: node.location_street ?? null,
    slug: node.slug ?? null,
    starts_at: node.starts_at ?? null,
    status: node.status ?? "",
    title: node.title ?? null,
    visibility: node.visibility ?? "",
  };
}
